import React from "react";
import {
  ActionButton,
  FontWeights,
  getTheme,
  Label,
  mergeStyleSets,
  MessageBar,
  MessageBarType,
  Modal,
  PrimaryButton,
  Stack,
  TextField,
} from "office-ui-fabric-react";
import { Formik, FormikErrors } from "formik";
import { isValidPassword, parseJwt } from "../../services/UtilService";
import {
  changeExpiredPasswordForAdmin,
  changeExpiredPasswordForTeam,
} from "../../services/auth/AuthService";
import { IUpdatePassword } from "../../vm";
import PasswordPolicy from "../common/PasswordPolicy";
import "../auth/login.css";

interface showPasswordChangeAlertFunc {
  (params: { isPolicyChangedUpdate?: boolean; expireId?: number }): void;
}

export interface IPasswordChangeAlert {
  showPasswordChangeAlert: showPasswordChangeAlertFunc;
}

export const PasswordChangeAlertContext = React.createContext<IPasswordChangeAlert>(
  { showPasswordChangeAlert: () => {} }
);

export const PasswordChangeAlertProvider: React.FC<any> = (props) => {
  const [state, setState] = React.useState<{
    isPolicyChangedUpdate?: boolean;
    expireId?: number;
  }>({
    isPolicyChangedUpdate: false,
    expireId: undefined,
  });
  const [showUpdatePassword, setShowUpdatePassword] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [isChangedPassword, setIsChangedPassword] = React.useState(false);

  const passwordChangeAlertContext = React.useMemo(
    () => ({
      showPasswordChangeAlert: ({
        isPolicyChangedUpdate,
        expireId,
      }: {
        isPolicyChangedUpdate?: boolean;
        expireId?: number;
      }) => {
        setState({
          isPolicyChangedUpdate: isPolicyChangedUpdate,
          expireId: expireId,
        });
      },
    }),
    []
  );

  const hideModel = () => {
    setState({ isPolicyChangedUpdate: false, expireId: undefined });
    setShowUpdatePassword(false);
    setErrorMsg("");
    setIsChangedPassword(false);
  };

  return (
    <PasswordChangeAlertContext.Provider value={passwordChangeAlertContext}>
      <>
        {(state.expireId != null || state.isPolicyChangedUpdate) && (
          <>
            <Modal
              titleAriaId={"title-id"}
              isOpen={true}
              onDismiss={hideModel}
              isBlocking={true}
              containerClassName={contentStyles.container}
            >
              <div className={contentStyles.header}>
                <span id={"title-id"}>
                  {state.isPolicyChangedUpdate
                    ? "We have changed our password policy. Consider Changing your password."
                    : `Your password will expire ${
                        state.expireId === 0
                          ? "today"
                          : "in " + state.expireId + " days"
                      }. Consider Changing your password.`}
                </span>
              </div>
              <div className={contentStyles.body}>
                <br />
                {showUpdatePassword ? (
                  isChangedPassword ? (
                    <MessageBar
                      messageBarType={MessageBarType.success}
                      isMultiline={false}
                    >
                      Password updated Successfully.
                    </MessageBar>
                  ) : (
                    <Formik
                      initialValues={{ newPassword: "", confirmPassword: "" }}
                      validate={(values) => {
                        let errors: FormikErrors<IUpdatePassword> = {};

                        if (!values.newPassword) {
                          errors.newPassword = "Required";
                        }
                        if (
                          values.newPassword &&
                          !isValidPassword(values.newPassword)
                        ) {
                          errors.newPassword = "Please enter valid password.";
                        }
                        if (!values.confirmPassword) {
                          errors.confirmPassword = "Required";
                        }
                        if (
                          values.newPassword &&
                          values.confirmPassword &&
                          values.newPassword !== values.confirmPassword
                        ) {
                          errors.confirmPassword = "Password does not match";
                        }
                        return errors;
                      }}
                      onSubmit={async (
                        values: {
                          newPassword: string;
                          confirmPassword: string;
                        },
                        { setSubmitting, setFieldValue }
                      ) => {
                        const tokenData = parseJwt();

                        let result;
                        if (tokenData.isAdmin) {
                          result = await changeExpiredPasswordForAdmin(
                            values.newPassword
                          );
                        } else {
                          result = await changeExpiredPasswordForTeam(
                            values.newPassword
                          );
                        }
                        if (result && result.success) {
                          setIsChangedPassword(true);
                          setTimeout(hideModel, 2 * 1000);
                          // hideModel();
                        } else {
                          if (
                            result.message &&
                            result.message.includes(
                              "Please use different password"
                            )
                          ) {
                            setErrorMsg(
                              "Sorry, but this password has already been used. Please create another."
                            );
                            setFieldValue("newPassword", "");
                            setFieldValue("confirmPassword", "");
                          } else setErrorMsg(result.message as string);
                        }
                        setSubmitting(false);
                      }}
                    >
                      {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                        /* and other goodies */
                      }) => (
                        <form onSubmit={handleSubmit}>
                          <Label className="login-label">New Password</Label>
                          <TextField
                            id="newPassword"
                            name="newPassword"
                            // label="New Password"
                            autoComplete="new-password"
                            type="password"
                            onChange={handleChange}
                            errorMessage={
                              errors.newPassword && touched.newPassword
                                ? errors.newPassword
                                : ""
                            }
                            onBlur={handleBlur}
                            value={values.newPassword}
                            // className="custom-textfield"
                            underlined
                          />
                          {errors.newPassword &&
                            touched.newPassword &&
                            values.newPassword && (
                              <PasswordPolicy password={values.newPassword} />
                            )}
                          <Label className="login-label">
                            Confirm Password
                          </Label>
                          <TextField
                            id="confirmPassword"
                            name="confirmPassword"
                            // label="Confirm Password"
                            autoComplete="confirm-password"
                            type="password"
                            onChange={handleChange}
                            errorMessage={
                              errors.confirmPassword && touched.confirmPassword
                                ? errors.confirmPassword
                                : ""
                            }
                            onBlur={handleBlur}
                            value={values.confirmPassword}
                            // className="custom-textfield"
                            underlined
                          />
                          <br />
                          {errorMsg && (
                            <>
                              <MessageBar
                                messageBarType={MessageBarType.error}
                                dismissButtonAriaLabel="Close"
                                onDismiss={() => {
                                  setErrorMsg("");
                                }}
                                isMultiline={true}
                              >
                                {errorMsg}
                              </MessageBar>
                              <br />
                            </>
                          )}
                          <PrimaryButton
                            type="submit"
                            // color="primary"
                            disabled={isSubmitting}
                            className="login-btn width-auto"
                          >
                            Update Password
                          </PrimaryButton>
                        </form>
                      )}
                    </Formik>
                  )
                ) : (
                  <Stack
                    horizontal
                    horizontalAlign="start"
                    verticalAlign="center"
                  >
                    <Stack.Item>
                      <PrimaryButton
                        onClick={() => {
                          setShowUpdatePassword(true);
                        }}
                      >
                        Change Password
                      </PrimaryButton>
                    </Stack.Item>
                    {state.isPolicyChangedUpdate ? (
                      <Stack.Item>
                        <ActionButton onClick={hideModel}>
                          Skip for now
                        </ActionButton>
                      </Stack.Item>
                    ) : (
                      state.expireId &&
                      state.expireId > 2 && (
                        <Stack.Item>
                          <ActionButton onClick={hideModel}>
                            Skip for now
                          </ActionButton>
                        </Stack.Item>
                      )
                    )}
                  </Stack>
                )}
              </div>
            </Modal>
          </>
        )}
        {props.children}
      </>
    </PasswordChangeAlertContext.Provider>
  );
};

const theme = getTheme();
const contentStyles = mergeStyleSets({
  container: {
    display: "flex",
    flexFlow: "column nowrap",
    alignItems: "stretch",
  },

  header: [
    theme.fonts.xLargePlus,
    {
      flex: "1 1 auto",
      borderTop: `4px solid ${theme.palette.orange}`,
      color: theme.palette.neutralPrimary,
      display: "flex",
      alignItems: "center",
      fontWeight: FontWeights.semilight,
      padding: "12px 12px 14px 24px",
    },
  ],
  body: {
    flex: "4 4 auto",
    padding: "0 24px 24px 24px",
    overflowY: "hidden",
  },
});

export const withPasswordChangeAlertContext = (ChildComponent: any) => (
  props: any
) => {
  const { showPasswordChangeAlert } = React.useContext(
    PasswordChangeAlertContext
  );
  return (
    <ChildComponent
      {...props}
      showPasswordChangeAlert={showPasswordChangeAlert}
    />
  );
};
